import React, { useState, useEffect, useMemo } from 'react';
import { X, Layers, Loader, AlertCircle, ArrowRight } from 'lucide-react';
import { dataInspectionService } from '../../../../../services/dataInspectionService';
import { ItemBinsDto } from '../../../../../types/dataInspection';
import { ScanTextInput } from '../../../../common/inputs';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface LocationStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Varenummer på aktiv plukklinje. */
  itemNo: string;
  itemDescription?: string;
  /** Bin-koden det plukkes fra nå (markeres som gjeldende). */
  currentBinCode: string;
  /** Lager linja tilhører — begrenser visningen til samme lager. */
  warehouseId?: string;
  /** Gjenstående å plukke (qty - qtyPicked). Brukes for å gate «Plukk herfra». */
  requiredQuantity: number;
  /** false = vare-GO (ERP eier saldo) → ingen ItemBins/saldo-gate. */
  isStockControlled?: boolean;
  /** Kalles når operatøren velger en annen lokasjon å plukke fra. Forelder gjør
   *  den kanoniske overstyringen (samme som «vanlig» override) + lukker modalen. */
  onOverride: (newBinCode: string) => void;
}

/** Aggregert saldo pr. lokasjon (slår sammen serie-/batch-rader for samme bin). */
interface BinStockRow {
  binCode: string;
  binDescription?: string;
  quantity: number;
  reserved: number;
  available: number;
  /** false = lokasjonen er ikke reserverbar (StcBin.ReservationAllowed=0) → kan ikke plukkes fra. */
  reservable: boolean;
}

/**
 * LocationStockModal — viser hvor varen på aktiv plukklinje ligger: antall, reservert og
 * tilgjengelig PR. lokasjon, med gjeldende plukklokasjon uthevet. Operatøren kan eventuelt
 * overstyre plukklokasjonen herfra («Plukk herfra» eller skann en lokasjon) — overstyringen
 * går via forelderens kanoniske `updateBinCode`-flyt slik at den reflekteres nedstrøms
 * nøyaktig som en «vanlig» lokasjons-overstyring.
 *
 * Saldo hentes fra `getItemBinsByItemNo` (ItemBins + StcReservation, ResStatus=0). Ingen
 * nye endepunkter. Tema-kompatibel + nettbrett-vennlig.
 */
export const LocationStockModal: React.FC<LocationStockModalProps> = ({
  isOpen,
  onClose,
  itemNo,
  itemDescription,
  currentBinCode,
  warehouseId,
  requiredQuantity,
  isStockControlled = true,
  onOverride
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rawBins, setRawBins] = useState<ItemBinsDto[]>([]);
  const [scanValue, setScanValue] = useState('');

  // Hent saldo pr. lokasjon når modalen åpnes
  useEffect(() => {
    if (!isOpen || !itemNo) return;
    let cancelled = false;

    setLoading(true);
    setError(null);
    setScanValue('');
    dataInspectionService.getItemBinsByItemNo(itemNo)
      .then(bins => { if (!cancelled) setRawBins(bins); })
      .catch(err => {
        console.error('Kunne ikke laste lokasjons-saldo:', err);
        if (!cancelled) setError(t('warehouse.modules.picking.locationStock.loadError', {}, 'Kunne ikke laste lokasjoner'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [isOpen, itemNo, t]);

  // Aggregér pr. binCode (serie-/batch-rader deler bin), filtrer på lager, sorter gjeldende først
  const rows = useMemo<BinStockRow[]>(() => {
    const byBin = new Map<string, BinStockRow>();
    for (const ib of rawBins) {
      if (warehouseId && ib.warehouseID && ib.warehouseID !== warehouseId) continue;
      const key = (ib.binCode || '').toLowerCase();
      if (!key) continue;
      const existing = byBin.get(key);
      if (existing) {
        existing.quantity += ib.quantity ?? 0;
        existing.reserved += ib.quantityReserved ?? 0;
        existing.available += ib.quantityAvailable ?? 0;
        // Per-bin-flagg; alle rader for samme bin deler verdi, men vær defensiv (AND).
        if (ib.reservationAllowed === false) existing.reservable = false;
      } else {
        byBin.set(key, {
          binCode: ib.binCode,
          binDescription: ib.binDescription,
          quantity: ib.quantity ?? 0,
          reserved: ib.quantityReserved ?? 0,
          available: ib.quantityAvailable ?? 0,
          reservable: ib.reservationAllowed !== false
        });
      }
    }
    const curr = currentBinCode.toLowerCase();
    // Skjul lokasjoner med saldo 0 — tomme ItemBins-rader slettes aldri etter plukk og gir
    // ellers en lang liste med steder det ikke kan plukkes fra. Gjeldende plukklokasjon vises
    // alltid (også ved overreservasjon), og vare-GO (ERP eier saldo) filtreres ikke.
    const list = Array.from(byBin.values()).filter(r =>
      !isStockControlled || r.quantity > 0 || r.binCode.toLowerCase() === curr);
    list.sort((a, b) => {
      const aCurr = a.binCode.toLowerCase() === curr ? 1 : 0;
      const bCurr = b.binCode.toLowerCase() === curr ? 1 : 0;
      if (aCurr !== bCurr) return bCurr - aCurr;       // gjeldende først
      return b.available - a.available;                 // deretter mest tilgjengelig
    });
    return list;
  }, [rawBins, warehouseId, currentBinCode, isStockControlled]);

  if (!isOpen) return null;

  const canPickFrom = (row: BinStockRow): boolean =>
    row.binCode.toLowerCase() !== currentBinCode.toLowerCase()
    && row.reservable
    && (!isStockControlled || row.available >= requiredQuantity);

  const handleScanOverride = () => {
    const scanned = scanValue.trim();
    if (!scanned) return;
    const match = rows.find(r => r.binCode.toLowerCase() === scanned.toLowerCase());
    if (!match) {
      setError(t('warehouse.modules.picking.locationStock.scanNotFound', { 0: scanned }, `Fant ikke varen på lokasjon ${scanned}`));
      return;
    }
    if (!match.reservable) {
      setError(t('warehouse.modules.picking.locationStock.scanNotReservable', { 0: scanned }, `Lokasjon ${scanned} er ikke reserverbar og kan ikke velges`));
      return;
    }
    if (!canPickFrom(match)) {
      setError(t('warehouse.modules.picking.locationStock.scanInsufficient', { 0: scanned }, `Lokasjon ${scanned} har ikke nok tilgjengelig`));
      return;
    }
    onOverride(match.binCode);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-xl">
            <div className="flex items-center gap-3 min-w-0">
              <Layers className="w-5 h-5 text-white flex-shrink-0" />
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-white truncate">
                  {t('warehouse.modules.picking.locationStock.title', {}, 'Lokasjoner')}: {itemNo}
                </h2>
                {itemDescription && <p className="text-sm text-blue-100 truncate">{itemDescription}</p>}
              </div>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white transition-colors min-h-[44px] px-2">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 space-y-4 overflow-y-auto">
            {/* Valgfri skann-til-overstyring */}
            {isStockControlled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('warehouse.modules.picking.locationStock.scanLabel', {}, 'Skann lokasjon for å plukke herfra')}
                </label>
                <div className="flex gap-2">
                  <ScanTextInput
                    value={scanValue}
                    onChange={(e) => { setScanValue(e.target.value); if (error) setError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleScanOverride(); } }}
                    placeholder={t('warehouse.modules.picking.locationStock.scanPlaceholder', {}, 'Skann eller skriv lokasjon...')}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[44px]"
                  />
                  <button
                    onClick={handleScanOverride}
                    disabled={!scanValue.trim()}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-500 min-h-[44px]"
                  >
                    {t('warehouse.modules.picking.locationStock.useLocation', {}, 'Bruk')}
                  </button>
                </div>
              </div>
            )}

            {error && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /> {error}
              </p>
            )}

            {loading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-gray-500">
                <Loader className="w-5 h-5 animate-spin" /> {t('common.status.loading', {}, 'Laster...')}
              </div>
            ) : rows.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-500">
                {t('warehouse.modules.picking.locationStock.noLocations', {}, 'Varen ligger ikke på noen lokasjon med saldo.')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-medium text-gray-500 uppercase border-b border-gray-200">
                      <th className="px-2 py-2">{t('warehouse.modules.picking.locationStock.colLocation', {}, 'Lokasjon')}</th>
                      <th className="px-2 py-2 text-right">{t('warehouse.modules.picking.locationStock.colQuantity', {}, 'Antall')}</th>
                      <th className="px-2 py-2 text-right">{t('warehouse.modules.picking.locationStock.colReserved', {}, 'Reservert')}</th>
                      <th className="px-2 py-2 text-right">{t('warehouse.modules.picking.locationStock.colAvailable', {}, 'Tilgjengelig')}</th>
                      <th className="px-2 py-2 w-28"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((row) => {
                      const isCurrent = row.binCode.toLowerCase() === currentBinCode.toLowerCase();
                      return (
                        <tr key={row.binCode} className={isCurrent ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                          <td className="px-2 py-2">
                            <div className="font-mono font-medium text-gray-900">{row.binCode}</div>
                            {isCurrent && (
                              <span className="text-xs text-blue-700">{t('warehouse.modules.picking.locationStock.current', {}, 'plukkes fra')}</span>
                            )}
                            {!isCurrent && row.binDescription && (
                              <span className="text-xs text-gray-500">{row.binDescription}</span>
                            )}
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums">{row.quantity}</td>
                          <td className="px-2 py-2 text-right tabular-nums text-amber-700">{row.reserved}</td>
                          <td className="px-2 py-2 text-right tabular-nums font-medium text-gray-900">{row.available}</td>
                          <td className="px-2 py-2 text-right">
                            {!isCurrent && (
                              !row.reservable ? (
                                <span className="inline-block text-xs text-gray-400 italic">
                                  {t('warehouse.modules.picking.locationStock.notReservable', {}, 'Ikke reserverbar')}
                                </span>
                              ) : (
                                <button
                                  onClick={() => onOverride(row.binCode)}
                                  disabled={!canPickFrom(row)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed min-h-[36px]"
                                  title={canPickFrom(row) ? undefined : t('warehouse.modules.picking.locationStock.notEnough', {}, 'Ikke nok tilgjengelig')}
                                >
                                  {t('warehouse.modules.picking.locationStock.pickFromHere', {}, 'Plukk herfra')}
                                  <ArrowRight className="w-3 h-3" />
                                </button>
                              )
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-xl flex items-center justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 min-h-[44px]"
            >
              {t('common.buttons.close', {}, 'Lukk')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationStockModal;
